import { Signal, signal, WritableSignal } from '@angular/core';
import { FormControl, NgControl, ValidationErrors } from '@angular/forms';

/**
 * Unified Validation State Manager
 *
 * Provides a single source of truth for validation state across all Harmony form controls. Solves the synchronization
 * issues between internal FormControl and external NgControl by:
 *
 * 1. Using a single computed signal (`showErrors`) as the ONLY source for error display decisions
 * 2. Properly ordering state updates: value → notify → validate
 * 3. Providing consistent APIs for all form controls
 *
 * @example
 *   ```typescript
 *   export class HxMyControl extends HxBaseControl {
 *     private _validationState = createValidationState({
 *       internalControl: this._internalControl,
 *       getNgControl: () => this.ngControl,
 *     });
 *
 *     // Use in template: @if (_validationState.showErrors())
 *     // Get errors: _validationState.errors()
 *   }
 *   ```;
 */

/** Configuration for creating a validation state manager */
export interface ValidationStateConfig {
  /** The internal FormControl used by the component */
  internalControl: FormControl<unknown>;

  /** Function to get the NgControl (may be null initially, resolved after DI) */
  getNgControl: () => NgControl | null;

  /** Optional: Custom function to determine if field has a value */
  hasValue?: () => boolean;
}

/** The validation state object returned by createValidationState */
export interface ValidationState {
  /** Whether the control is currently invalid (has errors) */
  readonly invalid: Signal<boolean>;

  /** Whether the control has been touched (blurred) */
  readonly touched: Signal<boolean>;

  /** Whether the control has been modified (dirty) */
  readonly dirty: Signal<boolean>;

  /**
   * SINGLE SOURCE OF TRUTH: Whether to display errors This is a function (not a signal) to ensure it always reads fresh
   * state from external controls when they are marked touched/dirty programmatically (e.g., form.markAllAsTouched() on
   * Save)
   */
  showErrors: () => boolean;

  /**
   * Current validation errors (from external control if available, otherwise internal) This is a function (not a
   * signal) to ensure fresh reads from external control
   */
  errors: () => ValidationErrors | null;

  /** Mark control as touched */
  markTouched: () => void;

  /** Mark control as dirty */
  markDirty: () => void;

  /** Reset all validation state */
  reset: () => void;

  /** Update validation state after a value change IMPORTANT: Call this AFTER notifying the external control (_onChange) */
  updateAfterValueChange: () => void;

  /** Sync state from external control (call in ngOnInit after ngControl is available) */
  syncFromExternalControl: () => void;
}

/**
 * Creates a unified validation state manager for a form control component
 *
 * @param config - Configuration object
 * @returns ValidationState object with reactive signals and methods
 */
export function createValidationState(config: ValidationStateConfig): ValidationState {
  const { internalControl, getNgControl, hasValue } = config;

  // Internal writable signals - these are the single source of truth
  const _touched: WritableSignal<boolean> = signal(false);
  const _dirty: WritableSignal<boolean> = signal(false);
  const _invalid: WritableSignal<boolean> = signal(false);
  const _errors: WritableSignal<ValidationErrors | null> = signal(null);

  /** Updates the invalid and errors state by checking external control first, then falling back to internal */
  const _updateValidityState = (): void => {
    const externalControl = getNgControl()?.control;

    if (externalControl) {
      // External control is the source of truth for validity
      _invalid.set(externalControl.invalid);
      _errors.set(externalControl.errors);
    } else {
      // Fall back to internal control
      _invalid.set(internalControl.invalid);
      _errors.set(internalControl.errors);
    }
  };

  /**
   * Checks if control is touched - checks BOTH local state AND external control This is critical for programmatic
   * touches (e.g., form.markAllAsTouched() on Save)
   */
  const _isTouched = (): boolean => {
    const externalControl = getNgControl()?.control;
    return _touched() || (externalControl?.touched ?? false);
  };

  /** Checks if control is dirty - checks BOTH local state AND external control */
  const _isDirty = (): boolean => {
    const externalControl = getNgControl()?.control;
    return _dirty() || (externalControl?.dirty ?? false);
  };

  /** Checks if control is invalid - checks external control first, then internal */
  const _isInvalid = (): boolean => {
    const externalControl = getNgControl()?.control;
    return externalControl?.invalid ?? _invalid();
  };

  /** Gets current errors - checks external control first, then internal */
  const _getErrors = (): ValidationErrors | null => {
    const externalControl = getNgControl()?.control;
    return externalControl?.errors ?? _errors();
  };

  /**
   * SINGLE SOURCE OF TRUTH for error display This is a FUNCTION (not computed signal) to ensure fresh reads of external
   * control state. Critical for programmatic touches (e.g., form.markAllAsTouched() on "Save and Exit")
   *
   * Errors are shown when:
   *
   * 1. Control is invalid AND
   * 2. Control has been interacted with (touched OR dirty)
   */
  const showErrors = (): boolean => {
    return _isInvalid() && (_isTouched() || _isDirty());
  };

  return {
    // Read-only signals
    invalid: _invalid.asReadonly(),
    touched: _touched.asReadonly(),
    dirty: _dirty.asReadonly(),

    // Functions that read fresh state from external control
    showErrors,
    errors: _getErrors,

    // Methods
    markTouched: () => {
      _touched.set(true);
      internalControl.markAsTouched();
      _updateValidityState();
    },

    markDirty: () => {
      _dirty.set(true);
      internalControl.markAsDirty();
      _updateValidityState();
    },

    reset: () => {
      _touched.set(false);
      _dirty.set(false);
      _invalid.set(false);
      _errors.set(null);
      internalControl.reset();
    },

    /** Call this AFTER _onChange(newValue) to ensure external control has received the value */
    updateAfterValueChange: () => {
      // Give Angular a chance to process the value change in external control
      // Then update our validity state from the authoritative source
      _updateValidityState();
    },

    /** Syncs initial state from external control (call in ngOnInit) */
    syncFromExternalControl: () => {
      const externalControl = getNgControl()?.control;
      if (externalControl) {
        if (externalControl.touched) _touched.set(true);
        if (externalControl.dirty) _dirty.set(true);
        _updateValidityState();
      }
    },
  };
}

/**
 * Helper to get error message for a specific validation error type
 *
 * @param errors - Current validation errors
 * @param errorType - The error key to check for
 * @param customMessages - Optional custom messages keyed by error type
 * @param defaultMessages - Default messages keyed by error type
 * @returns Error message string or empty string if no error
 */
export function getErrorMessage(
  errors: ValidationErrors | null,
  errorType: string,
  customMessages?: Record<string, string>,
  defaultMessages?: Record<string, string | ((error: unknown) => string)>
): string {
  if (!errors || !errors[errorType]) {
    return '';
  }

  // Check custom message first
  if (customMessages?.[errorType]) {
    return customMessages[errorType];
  }

  // Use default message
  const defaultMsg = defaultMessages?.[errorType];
  if (typeof defaultMsg === 'function') {
    return defaultMsg(errors[errorType]);
  }
  if (defaultMsg) {
    return defaultMsg;
  }

  return '';
}

/** Standard default error messages for common validation errors */
export const DEFAULT_ERROR_MESSAGES: Record<string, string | ((error: unknown) => string)> = {
  required: 'This field is required',
  email: 'Invalid email address',
  minlength: (error: unknown) => {
    const e = error as { requiredLength?: number };
    return `Minimum length is ${e?.requiredLength ?? 0} characters`;
  },
  maxlength: (error: unknown) => {
    const e = error as { requiredLength?: number };
    return `Maximum length is ${e?.requiredLength ?? 0} characters`;
  },
  min: (error: unknown) => {
    const e = error as { min?: number };
    return `Minimum value is ${e?.min ?? 0}`;
  },
  max: (error: unknown) => {
    const e = error as { max?: number };
    return `Maximum value is ${e?.max ?? 0}`;
  },
  pattern: 'Invalid format',
  alphabetical: 'Only alphabetical characters (a-z, A-Z) are allowed',
  numerical: 'Only numbers (0-9) are allowed',
  specialCharacter: 'Invalid characters detected',
};
